/**
 * `/call/$code` — resolving a join code into a live call.
 *
 * The route is deliberately thin. Its whole job is to answer one question —
 * "do we hold a usable grant for this code?" — and then either show the door
 * or show the room. Everything about media, layout and LiveKit is below it in
 * `features/call/`.
 *
 * The call is addressed by JOIN CODE and not by session id, because the code
 * is the only identifier in this system a human can carry. The session id is
 * derived from it server-side.
 *
 * `LiveCall` is loaded lazily. That is what makes the `livekit` chunk in
 * `vite.config.ts` real: with a static import, the ~400 KB SDK would be part
 * of the module graph of every page including the landing page, and splitting
 * it into its own chunk would only change which file it arrives in.
 */

import * as React from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { formatJoinCode, normalizeJoinCode, type JoinGrant, type RoomResolution } from '@era/shared'
import { ApiError, api } from '@/lib/api'
import { AddToHomeScreenHint, useImmersiveCall } from '@/lib/immersive-call'
import { keys } from '@/lib/query'
import { cn } from '@/lib/utils'
import {
  grantNeedsResume,
  invalidateLiveGrant,
  prepareGrantResume,
  readGrant,
  readCreatorControl,
  recallName,
  rememberName,
  stashGrant,
  takePreflight,
  type HeldGrant,
  type PreflightMedia,
} from '@/lib/livekit-handoff'
import { resumeStoredGrant } from '@/lib/resume-flow'
import { Prejoin } from '@/features/call/prejoin'
import { SAFE_PAD_6 } from '@/features/call/safe-area'

const LiveCall = React.lazy(async () => {
  const module = await import('@/features/call/live-call')
  return { default: module.LiveCall }
})

type Phase =
  | { kind: 'door' }
  | { kind: 'resuming'; held: HeldGrant; preflight: PreflightMedia | null; attempt: number }
  | {
      kind: 'resume_failed'
      held: HeldGrant
      preflight: PreflightMedia | null
      failure: { title: string; detail: string }
      attempt: number
    }
  | { kind: 'live'; held: HeldGrant; preflight: PreflightMedia | null }

export function CallRoute() {
  const { code: raw } = useParams({ from: '/call/$code' })

  // Normalised before anything else touches it: `k7m-2qx` from a chat message
  // and `K7M2QX` are the same room, and the server should never see the
  // difference.
  const code = normalizeJoinCode(raw)

  const [phase, setPhase] = React.useState<Phase>(() => {
    if (!code) return { kind: 'door' }
    const held = readGrant(code)
    // Came through the lobby, or reloaded before this grant completed its one
    // successful media connection. After that, reload must authenticate an
    // identity-preserving exchange for a fresh LiveKit token and QoE UUID; it
    // must not restart cumulative telemetry OR silently create a new identity.
    // The lobby also left its camera in the handoff store; taking it here is
    // what stops the camera light blinking between the two screens.
    if (!held) return { kind: 'door' }
    const preflight = takePreflight()
    return grantNeedsResume(held)
      ? { kind: 'resuming', held, preflight, attempt: 0 }
      : { kind: 'live', held, preflight }
  })

  const [joining, setJoining] = React.useState(false)
  const [error, setError] = React.useState<{ title: string; detail: string } | null>(null)
  const resumeStarted = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!code || phase.kind !== 'resuming') return
    let prepared: HeldGrant
    try {
      prepared = prepareGrantResume(code, phase.held)
    } catch (cause) {
      setPhase({
        kind: 'resume_failed',
        held: phase.held,
        preflight: phase.preflight,
        failure: describeResumeFailure(cause),
        attempt: phase.attempt,
      })
      return
    }
    const exchange = prepared.pending_resume
    if (!exchange) {
      setPhase({
        kind: 'resume_failed',
        held: prepared,
        preflight: phase.preflight,
        failure: {
          title: 'Could not prepare reconnect',
          detail: 'This browser could not create the rotating reconnect credentials.',
        },
        attempt: phase.attempt,
      })
      return
    }
    const attemptKey = `${exchange.request_id}:${phase.attempt}`
    // React StrictMode replays effects in development. Issuing twice could
    // waste the one bounded lost-response recovery and mint an unnecessary
    // LiveKit JWT, so one component attempt starts exactly one request.
    if (resumeStarted.current === attemptKey) return
    resumeStarted.current = attemptKey

    void resumeStoredGrant(code, prepared, (input, bearer) =>
      api<JoinGrant>(`/api/rooms/${encodeURIComponent(code)}/join`, {
        method: 'POST',
        // No identity/name is asserted by the browser, and the
        // diagnostics-only QoE bearer is intentionally absent.
        body: input,
        bearerToken: bearer,
      }),
    ).then((held) => {
      setPhase((current) =>
        current.kind === 'resuming' && current.attempt === phase.attempt
          ? { kind: 'live', held, preflight: phase.preflight }
          : current,
      )
    }).catch((cause: unknown) => {
      setPhase((current) =>
        current.kind === 'resuming' && current.attempt === phase.attempt
          ? {
              kind: 'resume_failed',
              held: prepared,
              preflight: phase.preflight,
              failure: describeResumeFailure(cause),
              attempt: phase.attempt,
            }
          : current,
      )
    })
  }, [code, phase])

  const join = React.useCallback(
    async (displayName: string, preflight: PreflightMedia | null) => {
      if (!code) return

      // NO AUTOMATIC FULLSCREEN. Operator decision, 2026-08-09: joining a call
      // must not take over the whole screen on desktop without being asked.
      //
      // This used to call `enterFullscreenForCall()` here, and the placement
      // was load-bearing for a reason worth keeping written down: the request
      // had to be the FIRST statement, because `requestFullscreen()` needs
      // transient activation and the `await` below ends it. Anyone restoring
      // automatic fullscreen must put the call back here, ahead of the
      // network — not after the join resolves, where every browser refuses it.
      //
      // The capability itself is retained and still correct: `immersive.ts`
      // keeps the exit-tracking state machine, and `useImmersiveCall` below
      // still releases the screen on leave for a session the user put into
      // fullscreen themselves. Wiring a manual control in the call bar is the
      // supported way to offer this on desktop.
      //
      // Nothing changes on iPhone either way: the Fullscreen API does not
      // exist for elements there, so this was always a no-op on the one
      // platform that most wants the effect. That platform's answer is the
      // Home Screen install taught by `AddToHomeScreenHint` — see the
      // module header of `lib/immersive.ts` for the verification behind it.

      setJoining(true)
      setError(null)
      try {
        const grant = await api<JoinGrant>(`/api/rooms/${encodeURIComponent(code)}/join`, {
          method: 'POST',
          body: { display_name: displayName },
        })
        rememberName(displayName)
        // A fresh join response never contains creator authority. Preserve a
        // capability this same tab already held only when the resolved session
        // id matches, so refreshing an expired LiveKit token does not demote
        // the room creator.
        const controlToken = readCreatorControl(code, grant.session_id)
        const held: HeldGrant = {
          grant: controlToken ? { ...grant, control_token: controlToken } : grant,
        }
        stashGrant(held)
        setPhase({ kind: 'live', held, preflight })
      } catch (cause) {
        setError(describeJoinFailure(cause))
      } finally {
        setJoining(false)
      }
    },
    [code],
  )

  /**
   * Back to the door with the stored grant cleared.
   *
   * The failure this exists for is a token the node refused, and the ONLY
   * correct response to that is a freshly minted `{url, token}` pair. Retrying
   * with the same token, or keeping the URL and minting a new token against
   * it, are both the mistake the API's overview warns about.
   */
  const rejoin = React.useCallback(() => {
    setPhase({ kind: 'door' })
    setError(null)
  }, [])

  const retryResume = React.useCallback(() => {
    setPhase((current) =>
      current.kind === 'resume_failed'
        ? {
            kind: 'resuming',
            held: current.held,
            preflight: current.preflight,
            attempt: current.attempt + 1,
          }
        : current,
    )
  }, [])

  const joinAsNewIdentity = React.useCallback(() => {
    if (!code) return
    // This is an explicit recovery choice, never the automatic reload path.
    // Expiring the LiveKit half makes readGrant ignore it while leaving any
    // creator-control capability available for the same resolved session.
    invalidateLiveGrant(code)
    setError(null)
    setPhase({ kind: 'door' })
  }, [code])

  /**
   * Resolve the code BEFORE showing the door.
   *
   * `normalizeJoinCode` only proves the code is well FORMED. `ZZZZZZ` is a
   * perfectly legal six-character code for a room that has never existed, and
   * without this the door greeted it with "A call is waiting." and a Join
   * button — the 404 only arrived after the visitor had picked a name and
   * committed. Resolving first is also the only way the door can show the real
   * title and say that a room has already ended, and it costs nothing: this
   * route reads SQLite and mints no token, which is exactly what
   * `RoomResolution` was added for ("enough to render the join screen before
   * committing to a token").
   *
   * Only while we are at the door. Once a grant exists the room is resolved by
   * definition, and re-asking on every reconnect would be noise.
   */
  const resolution = useQuery<RoomResolution, ApiError>({
    // `code` is null for a malformed one, which `BadCode` answers below without
    // a round trip — `enabled` keeps the query from ever running in that case,
    // so the non-null assertion in the key and the URL is safe.
    queryKey: keys.room(code ?? ''),
    queryFn: () => api<RoomResolution>(`/api/rooms/${encodeURIComponent(code as string)}`),
    enabled: code !== null && phase.kind === 'door',
    staleTime: 30_000,
  })

  /**
   * Immersive mode's lifecycle: hold the screen awake while we are in a room,
   * and give fullscreen back on every way out — leaving, a refused token, a
   * failed join that never got in.
   *
   * Entering is not here. It cannot be: see the note in `join` above.
   */
  useImmersiveCall(phase.kind === 'live')

  if (!code) return <BadCode raw={raw} />

  if (phase.kind === 'resuming') return <Booting label="Reconnecting this participant…" />

  if (phase.kind === 'resume_failed') {
    return (
      <ResumeFailure
        failure={phase.failure}
        onRetry={retryResume}
        onJoinAsNew={joinAsNewIdentity}
      />
    )
  }

  if (phase.kind === 'door') {
    // A 404 is the definitive answer that this code names nothing. Every other
    // failure — the BFF being down, a blocked request — is about US, not about
    // the code, so it must not accuse the visitor of a bad link: fall through
    // to the door and let the join attempt produce the real error.
    if (resolution.error?.status === 404) return <UnknownRoom code={code} />

    if (resolution.isPending && !resolution.isFetched) return <Resolving />

    const room = resolution.data
    if (room && room.status === 'closed') return <RoomEnded room={room} />

    return (
      <>
        <Prejoin
          code={code}
          title={room?.title ?? null}
          host={room?.created_by_name ?? null}
          initialName={recallName()}
          busy={joining}
          error={error}
          onJoin={join}
        />
        {/* iPhone Safari only — the one platform with no Fullscreen API, where
            installing to the Home Screen is the only route to a chrome-free
            call. Nothing during a call: an install banner over live video is
            the thing this feature exists to remove, not to add.

            THIS IS THE SECONDARY MOUNT, and it used to be the only one, which
            is why nobody ever saw it. This branch is reachable only by someone
            who opens a shared `/call/CODE` link with no stored grant — never by
            anyone who came through the lobby, because `HomeRoute.enter()`
            stashes the grant before navigating and this route then opens
            straight at `{kind:'live'}`. The lobby carries the primary mount;
            this one exists for the link recipient who never sees the lobby.

            `floating` because the door below is `features/call/prejoin.tsx`,
            which this route does not own and must not have its layout reshaped
            for a dismissible hint. */}
        <AddToHomeScreenHint variant="floating" />
      </>
    )
  }

  return (
    <React.Suspense fallback={<Booting />}>
      <LiveCall
        grant={phase.held.grant}
        preflight={phase.preflight}
        onRejoin={rejoin}
      />
    </React.Suspense>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * Failures on the way in, each said as the thing that actually happened.
 *
 * The 503 is the one that matters: it means no node had capacity, it is
 * genuinely retryable, and the wrong response — picking a node, or reusing a
 * URL from an earlier room — produces a call that fails much later and much
 * more confusingly.
 */
function describeJoinFailure(cause: unknown): { title: string; detail: string } {
  if (!(cause instanceof ApiError)) {
    return { title: 'Could not join', detail: 'Something went wrong on the way into the room.' }
  }
  if (cause.noCapacity) {
    return {
      title: 'Every node is busy',
      detail:
        'No media node had capacity for this room right now. This clears on its own — try again in a moment.',
    }
  }
  if (cause.notImplemented) {
    return {
      title: 'This server cannot join rooms yet',
      detail: `${cause.message} — the route exists but has no implementation in this build.`,
    }
  }
  if (cause.status === 404) {
    return {
      title: 'No room with that code',
      detail: 'Codes are six characters. Check it, or ask for a new one.',
    }
  }
  if (cause.status === 409 || cause.status === 410) {
    return {
      title: 'That room has ended',
      detail: 'A join code stops working when the room closes. Ask for a new one.',
    }
  }
  if (cause.status === 0) {
    return {
      title: 'The server is not responding',
      detail: 'This app could not reach its own backend, so it never got as far as the call.',
    }
  }
  return { title: 'Could not join', detail: cause.message }
}

/**
 * One shell for every "you are not getting into a room" answer, so the four of
 * them cannot drift into looking like four different products.
 */
function Door({
  eyebrow,
  heading,
  children,
  action = 'Start a new call',
}: {
  eyebrow: string
  heading: React.ReactNode
  children: React.ReactNode
  action?: string
}) {
  return (
    /* Two layers, and the outer one is the scroller.
       `h-full` rather than `h-[100dvh]`: `#root` is already exactly the
       viewport (styles.css) and the call routes render straight into it, so a
       second viewport unit here is a second source of truth that can only
       disagree — and would, in landscape, where this copy is taller than a
       390px-high phone.

       The centring lives on an inner `min-h-full` box rather than on the
       scroller itself. `place-items-center` on an overflowing container
       centres the child around the middle and pushes its TOP out of reach:
       you can scroll down to the rest but never back up to the start. */
    <div className="h-full overflow-y-auto overscroll-contain bg-[#0b0a09] text-[#f3eee8]">
      <div className={cn('flex min-h-full items-center justify-center', SAFE_PAD_6)}>
        <div className="max-w-md space-y-3 text-center">
          <p className="text-[0.6875rem] font-semibold tracking-[0.1em] text-white/35 uppercase">
            {eyebrow}
          </p>
          <h1 className="display text-2xl text-white">{heading}</h1>
          <p className="text-sm leading-relaxed text-white/50">{children}</p>
          <Link
            to="/"
            className="mt-2 inline-flex h-11 items-center rounded-full bg-white/12 px-5 text-sm font-medium text-white transition-colors hover:bg-white/18 active:bg-white/22"
          >
            {action}
          </Link>
        </div>
      </div>
    </div>
  )
}

function BadCode({ raw }: { raw: string }) {
  return (
    <Door
      eyebrow="Not a join code"
      heading={
        <>
          <span className="ident">{raw}</span> is not six characters.
        </>
      }
    >
      Join codes leave out the characters people misread — no 0, 1, I, L, O or U — so a code with
      one of those in it was mistyped or mis-transcribed somewhere.
    </Door>
  )
}

/**
 * Well-formed, but it names nothing. Said BEFORE asking for a name, because
 * asking someone to introduce themselves to a room that does not exist is the
 * bug this screen was added to fix.
 */
function UnknownRoom({ code }: { code: string }) {
  return (
    <Door
      eyebrow="No such room"
      heading={
        <>
          Nothing is running under <span className="ident">{formatJoinCode(code)}</span>.
        </>
      }
    >
      The code is a valid shape, so it was not mistyped into something impossible — but no room
      answers to it. Codes are not reused, so this one was either finished long ago or copied
      incompletely.
    </Door>
  )
}

/** The room existed and is over. A join code stops working when the room closes. */
function RoomEnded({ room }: { room: RoomResolution }) {
  return (
    <Door eyebrow="Already finished" heading={room.title ?? 'That call has ended.'}>
      {room.created_by_name}&rsquo;s room closed, and a join code stops working the moment it does.
      If the call was recorded, the audio and transcript are on its{' '}
      <Link to="/session/$sessionId" params={{ sessionId: room.session_id }} className="underline">
        session page
      </Link>
      .
    </Door>
  )
}

/** The blink while the code is resolved. Local SQLite, so it is genuinely a blink. */
function Resolving() {
  return (
    <div className="grid h-full place-items-center bg-[#0b0a09] text-[#f3eee8]">
      <Loader2 className="size-5 animate-spin text-white/30" />
    </div>
  )
}

/** The half-second while the LiveKit chunk arrives. */
function Booting({ label }: { label?: string }) {
  return (
    <div className="grid h-full place-items-center bg-[#0b0a09] text-[#f3eee8]">
      <div className="flex items-center gap-3 text-sm text-white/45">
        <Loader2 className="size-5 animate-spin text-white/30" />
        {label ? <span>{label}</span> : null}
      </div>
    </div>
  )
}

function ResumeFailure({
  failure,
  onRetry,
  onJoinAsNew,
}: {
  failure: { title: string; detail: string }
  onRetry: () => void
  onJoinAsNew: () => void
}) {
  return (
    <div className="h-full overflow-y-auto overscroll-contain bg-[#0b0a09] text-[#f3eee8]">
      <div className={cn('flex min-h-full items-center justify-center', SAFE_PAD_6)}>
        <div className="max-w-md space-y-4 text-center">
          <p className="text-[0.6875rem] font-semibold tracking-[0.1em] text-white/35 uppercase">
            Secure reconnect
          </p>
          <h1 className="display text-2xl text-white">{failure.title}</h1>
          <p className="text-sm leading-relaxed text-white/50">{failure.detail}</p>
          <div className="flex flex-wrap justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-11 items-center rounded-full bg-white px-5 text-sm font-medium text-[#141210] transition-colors hover:bg-white/90"
            >
              Retry reconnect
            </button>
            <button
              type="button"
              onClick={onJoinAsNew}
              className="inline-flex h-11 items-center rounded-full bg-white/12 px-5 text-sm font-medium text-white transition-colors hover:bg-white/18"
            >
              Join with a new identity
            </button>
          </div>
          <p className="text-xs leading-relaxed text-white/30">
            Joining as new is explicit because it creates a separate transcript and quality lane.
          </p>
        </div>
      </div>
    </div>
  )
}

function describeResumeFailure(cause: unknown): { title: string; detail: string } {
  if (!(cause instanceof ApiError)) {
    return {
      title: 'Could not reconnect safely',
      detail: 'The identity-preserving credential exchange did not finish. Retry it before choosing a new identity.',
    }
  }
  if (cause.code === 'resume_in_progress') {
    return {
      title: 'Reconnect is already in progress',
      detail: 'Another copy of this reload is exchanging the same credential. Wait a moment, then retry.',
    }
  }
  if (cause.code === 'resume_replay_conflict') {
    return {
      title: 'Reconnect credential already used',
      detail: 'This one-time exchange already completed. It cannot be replayed without risking duplicate connections.',
    }
  }
  if (cause.code === 'room_closed' || cause.status === 410) {
    return {
      title: 'That room has ended',
      detail: 'The participant identity cannot reconnect after the room closes.',
    }
  }
  if (cause.status === 401) {
    return {
      title: 'Reconnect could not be verified',
      detail: 'The prior per-join capability is missing, expired, or does not belong to this room.',
    }
  }
  if (cause.status === 0) {
    return {
      title: 'The server is not responding',
      detail: 'The secure reconnect could not reach the backend. Retry before joining with a new identity.',
    }
  }
  return { title: 'Could not reconnect safely', detail: cause.message }
}
